import { BigMath } from "@yn/utils";
import { formatUnitPrice } from "./utils";

type PriceValue = string | number | null | undefined;
const CENT_ODDS_BASE_PRICE = 100;
const CENT_DEFAULT_UNIT_PRICE = 50;

/**
 * 获取当前货币单位下的默认预测单价。
 *
 * predik-next 当前单价单位固定按 CENT 理解，所以默认价格是 50¢。
 * 后续如果货币单位改为全局 store 控制，只需要在这里读取并切换默认值。
 *
 * @example
 * getDefaultUnitPrice() // 50
 */
export function getDefaultUnitPrice() {
    return CENT_DEFAULT_UNIT_PRICE;
}

/**
 * 读取预测单价，缺失时回退到当前货币单位的默认单价。
 *
 * @example
 * resolveUnitPrice( undefined ) // 50
 * resolveUnitPrice( 75 ) // 75
 */
export function resolveUnitPrice( price: PriceValue ) {
    if ( price === null || price === undefined || BigMath.lte( price, 0 ) ) {
        return getDefaultUnitPrice();
    }
    return price;
}

/**
 * 读取预测单价数字，缺失时回退到当前货币单位的默认单价。
 *
 * @example
 * resolveUnitPriceNumber( undefined ) // 50
 * resolveUnitPriceNumber( 75 ) // 75
 */
export function resolveUnitPriceNumber( price: PriceValue ) {
    return Number( resolveUnitPrice( price ) );
}

/**
 * 将预测合约的美分单价转换为概率百分比数字文本。
 *
 * 这里复用单价格式化能力，只隐藏美分符号，不做额外倍率换算。
 *
 * @example
 * convertCentPriceToChancePercent( 50 ) // "50"
 * convertCentPriceToChancePercent( 75 ) // "75"
 * convertCentPriceToChancePercent( 99 ) // "99"
 */
export function convertCentPriceToChancePercent( price: PriceValue, decimalPlaces = 0 ) {
    return formatUnitPrice( resolveUnitPrice( price ), {
        precision: decimalPlaces,
        showCurrencySymbol: false,
        useScale: false,
    } );
}

/**
 * 将预测合约的美分单价格式化为概率百分比文本。
 *
 * @example
 * formatCentPriceAsChancePercent( 50 ) // "50%"
 * formatCentPriceAsChancePercent( 75 ) // "75%"
 * formatCentPriceAsChancePercent( 99 ) // "99%"
 */
export function formatCentPriceAsChancePercent( price: PriceValue, decimalPlaces = 0 ) {
    return `${convertCentPriceToChancePercent( price, decimalPlaces )}%`;
}

/**
 * 将预测合约的美分单价格式化为十进制赔率倍数。
 *
 * 赔率由概率反推：赔率 = 1 / 概率。
 * 当前价格是美分时，50¢ 表示 50%，所以基准值应传 100，公式是 basePrice / price。
 *
 * @example
 * formatCentPriceAsOddsMultiplier( 50, 100 ) // "2.00×"
 * formatCentPriceAsOddsMultiplier( 25, 100 ) // "4.00×"
 * formatCentPriceAsOddsMultiplier( 0, 100 ) // "0.00×"
 */
export function formatCentPriceAsOddsMultiplier( price: PriceValue, basePrice: PriceValue ) {
    return `${BigMath.toFixed( BigMath.div( basePrice ?? 0, price ?? 0 ), 2 )}×`;
}

/**
 * 按当前单价单位格式化十进制赔率倍数。
 *
 * predik-next 当前单价单位固定按 CENT 理解，赔率基准统一使用 100。
 * 后续如果货币单位改为全局 store 控制，只需要在这里读取并切换基准值。
 *
 * @example
 * formatPriceAsOddsMultiplier( 50 ) // "2.00×"
 * formatPriceAsOddsMultiplier( 25 ) // "4.00×"
 */
export function formatPriceAsOddsMultiplier( price: PriceValue ) {
    return formatCentPriceAsOddsMultiplier( resolveUnitPrice( price ), CENT_ODDS_BASE_PRICE );
}

export function calculatePredictionPayoutCentAmount( quantity: PriceValue ) {
    return BigMath.gt( quantity, 0 ) ? BigMath.times( quantity, CENT_ODDS_BASE_PRICE ) : 0;
}
